import { useEffect } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { Col, Row, Spinner } from "reactstrap"

import { ISupportRequest } from "@api/schema"
import { SCROLLABLE_MAIN_PAGE } from "@components/BaseLayout"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import MarketStatsDisplay from "@components/marketplace/MarketStatsDisplay"
import { IFilteredCollectionState } from "@redux/helper/reducers"
import { EntityType } from "@redux/reduxTypes"
import { useEntityCollection } from "@services/hooks/useEntityCollection"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"
import { ISupportRequestFilterCriteria } from "@services/partnermarketHelper"

import SupportRequestCard from "./SupportRequestCard"

const usedNamespaces: NamespaceShortcut[] = ["partner-market", "supportRequest"]

interface IProps {
  showLoadingInformation?: boolean
  showTotalItemCount?: boolean
  filterCriteria?: ISupportRequestFilterCriteria
  withSupportOfferCreateButton?: boolean
  withLinkToSupportOffers?: boolean
  withLinkToProject?: boolean
  /** needed to get errors in order to display them on the page */
  setSupportRequestsLoadingState?: (SupportRequestsLoadingState: IFilteredCollectionState) => void
}

/**
 * This component provides a scroller for SupportRequests
 *
 */
const SupportRequestScroller: React.FC<IProps> = (props: IProps) => {
  const t = useDynamicTranslation()
  const {
    showLoadingInformation = false,
    showTotalItemCount = false,
    filterCriteria = {},
    withSupportOfferCreateButton = false,
    withLinkToSupportOffers = false,
    withLinkToProject = true,
    // returns the SupportRequestLoadingState to the parent component, e.g. to display an error
    // To get the SupportRequestLoadingState the usecaseKey is needed, but the component encapsulate the necessary usecasekey to load the SupportRequests
    // therefor exists this callback
    setSupportRequestsLoadingState
  } = props

  const { entities, request, loadNextPage } = useEntityCollection<ISupportRequest>(
    EntityType.SupportRequest,
    filterCriteria,
    // to avoid multiple API calls do not call the API before criteria is set
    { doNotLoad: !filterCriteria }
  )

  useEffect(() => {
    if (setSupportRequestsLoadingState) {
      setSupportRequestsLoadingState(request)
    }
  }, [JSON.stringify(request)])


  return <>
    {showTotalItemCount && request?.loaded &&
      <div className="text-left marketplace-stats">
        <MarketStatsDisplay
          count={request?.totalItems || 0}
          label={t("partner-market", "supportRequest.requestCount")}
        />
      </div>
    }
    {showLoadingInformation && request?.isLoading && <Spinner />}
    <InfiniteScroll
      dataLength={request?.totalItems}
      next={loadNextPage}
      hasMore={!!request?.nextLink}
      loader={null}
      scrollableTarget={SCROLLABLE_MAIN_PAGE}
    >
      <Row className="card-grid-layout">
        {entities.map((supportRequest, index) =>
          <SupportRequestCard
            key={"supportRequest_" + index.toString()}
            supportRequest={supportRequest}
            withSupportOfferCreateButton={withSupportOfferCreateButton}
            withLinkToSupportOffers={withLinkToSupportOffers}
            withLinkToProject={withLinkToProject}
          />
        )}
      </Row>
    </InfiniteScroll>
    {showLoadingInformation
      && request?.loaded
      && request?.totalItems === 0
      && <Row><Col>{t("partner-market", "supportRequest.noEntriesFound")}</Col></Row>
    }
  </>
}


export default withDynamicNamespaces<IProps>(SupportRequestScroller, usedNamespaces)
